import { useCallback, useEffect, useRef, useState } from "react";
import {
  FieldErrors,
  collectFieldErrors,
  focusFirstError,
} from "../render/collectFieldErrors";
import { mergeRuleErrors } from "../render/collectRuleErrors";

// Shared, so a caller with no validators doesn't hand the effect a new object each render.
const NoValidators: Record<string, string> = {};
const NoFields: string[] = [];

const without = (errors: FieldErrors, skip: string[]): FieldErrors =>
  skip.length
    ? Object.fromEntries(Object.entries(errors).filter(([name]) => !skip.includes(name)))
    : errors;

/** The form's field errors: none until a save is attempted, then kept up to date. */
export function useEntityPageValidation({
  fieldLabels,
  fieldValidatorKeys = NoValidators,
  record,
  id,
  formId,
  stepOnly = false,
}: {
  fieldLabels: Record<string, string>;
  /** The validator each field names, by field name. */
  fieldValidatorKeys?: Record<string, string>;
  record: Record<string, unknown> | null;
  id: string | null;
  formId: string;
  /** Only the fields in ieldLabels count, for a wizard step that is not the whole form. */
  stepOnly?: boolean;
}) {
  const formRef = useRef<HTMLFormElement>(null);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  // Errors are shown from the first save attempt onwards, not before it.
  const [validationAttempted, setValidationAttempted] = useState(false);
  const [validationAttempts, setValidationAttempts] = useState(0);
  // The fields the last attempt left out, e.g. for a save that doesn't need them.
  const [skipped, setSkipped] = useState<string[]>(NoFields);

  const collect = useCallback(
    (skip: string[]) =>
      without(mergeRuleErrors(
        stepOnly
          ? Object.fromEntries(
              Object.entries(collectFieldErrors(formRef.current, fieldLabels)).filter(
                ([name]) => name in fieldLabels
              )
            )
          : collectFieldErrors(formRef.current, fieldLabels),
        record,
        fieldValidatorKeys,
        Object.keys(fieldLabels)
      ), skip),
    [fieldLabels, fieldValidatorKeys, record, stepOnly]
  );

  // Errors appear on the first save attempt and then track what the user types, so a
  // field clears the moment it is fixed rather than at the next attempt.
  useEffect(() => {
    if (!validationAttempted) {
      return;
    }

    setFieldErrors(collect(skipped));
  }, [validationAttempted, collect, skipped]);

  // A different record, or a different view of one, is a fresh start.
  useEffect(() => {
    setValidationAttempted(false);
    setFieldErrors({});
  }, [id, formId]);

  /** Shows the form's errors, bar those skipped, and focuses the first; true when there are none. */
  const validate = useCallback((skip: string[] = NoFields) => {
    // `noValidate` is on the form, so this is the whole of the user's feedback.
    const found = collect(skip);

    setSkipped(skip);
    setValidationAttempted(true);
    setValidationAttempts((count) => count + 1);
    setFieldErrors(found);

    if (Object.keys(found).length > 0) {
      focusFirstError(formRef.current, found);

      return false;
    }

    return true;
  }, [collect]);

  /** Back to showing nothing, as before a save was tried. */
  const resetValidation = useCallback(() => {
    setValidationAttempted(false);
    setFieldErrors({});
  }, []);

  return { formRef, fieldErrors, validationAttempts, validate, resetValidation };
}
